import { useState } from 'react';
import { Send, AlertCircle, CheckCircle } from 'lucide-react';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Button from '../ui/Button';
import { useLanguage } from '../../context/LanguageContext';
import { reservationTimes } from '../../data/openingHours';

export default function ReservationForm() {
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    guests: '',
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Demo - just show success message
    setSubmitted(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Get tomorrow's date as minimum date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex p-4 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4">
          <CheckCircle className="w-12 h-12 text-primary-600 dark:text-primary-400" />
        </div>
        <h3 className="text-2xl font-display font-bold text-gray-900 dark:text-white mb-4">
          {t('reservationPage.success')}
        </h3>
        <Button onClick={() => setSubmitted(false)} variant="outline">
          Neue Reservierung
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Date and Time Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          type="date"
          name="date"
          label={t('reservationPage.form.date')}
          value={formData.date}
          onChange={handleChange}
          min={minDate}
          required
        />
        <Select
          name="time"
          label={t('reservationPage.form.time')}
          value={formData.time}
          onChange={handleChange}
          required
        >
          <option value="">-- Uhrzeit wählen --</option>
          {reservationTimes.map(time => (
            <option key={time} value={time}>{time}</option>
          ))}
        </Select>
      </div>

      {/* Guests */}
      <Select
        name="guests"
        label={t('reservationPage.form.guests')}
        value={formData.guests}
        onChange={handleChange}
        required
      >
        <option value="">{t('reservationPage.form.guestsPlaceholder')}</option>
        {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
          <option key={num} value={num}>
            {num} {num === 1 ? t('reservationPage.form.person') : t('reservationPage.form.persons')}
          </option>
        ))}
      </Select>

      {/* Contact Info */}
      <Input
        type="text"
        name="name"
        label={t('reservationPage.form.name')}
        placeholder={t('reservationPage.form.namePlaceholder')}
        value={formData.name}
        onChange={handleChange}
        required
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          type="email"
          name="email"
          label={t('reservationPage.form.email')}
          placeholder={t('reservationPage.form.emailPlaceholder')}
          value={formData.email}
          onChange={handleChange}
          required
        />
        <Input
          type="tel"
          name="phone"
          label={t('reservationPage.form.phone')}
          placeholder={t('reservationPage.form.phonePlaceholder')}
          value={formData.phone}
          onChange={handleChange}
          required
        />
      </div>

      {/* Notes */}
      <Textarea
        name="notes"
        label={t('reservationPage.form.notes')}
        placeholder={t('reservationPage.form.notesPlaceholder')}
        value={formData.notes}
        onChange={handleChange}
        rows={3}
      />

      {/* Demo Notice */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700 dark:text-amber-300">
          {t('reservationPage.demoNotice')}
        </p>
      </div>

      {/* Submit Button */}
      <Button type="submit" className="w-full" size="lg">
        <Send className="w-5 h-5" />
        {t('reservationPage.form.submit')}
      </Button>
    </form>
  );
}
